import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { useDebounce } from 'use-debounce';

import { ErrorMessage } from '../../../../components';
import { useAuth } from '../../../../context';
import {
  VerificationRequestsVariables,
  VerificationRequests_requestVerificationAdvance_row,
} from '../../../../generated/VerificationRequests';
import {
  FilterStoreStatusVerifyInput,
  FilterUserStatusVerifyInput,
  FilterViewDataInput,
  TypeVerify,
} from '../../../../generated/globalTypes';
import { toDdMmYyyy } from '../../../../helpers';
import { CustomNetworkError } from '../../../../types/errorTypes';
import { colors, spacing } from '../../constants/theme';
import { Filter, FilterModal, Text, TextInput } from '../../core-ui';
import { VerificationRequestsQueryResult } from '../TokoVerification';

type ListItemProps = {
  name: string;
  date: string;
  type: string;
  city: string;
  selected: boolean;
  onSelect: () => void;
};

const ListItem = (props: ListItemProps) => {
  let { name, date, type, city, selected, onSelect } = props;
  let selectedStyle =
    type === 'TOKO' ? styles.selectedToko : styles.selectedOwner;
  return (
    <TouchableWithoutFeedback onPress={onSelect}>
      <View
        data-testid="left-pane-item"
        style={[styles.listItem, selected && selectedStyle]}
      >
        <Text bold size="m" style={styles.largeBottomSpacing}>
          {name}
        </Text>
        <View
          style={[styles.row, styles.spaceBetween, styles.smallBottomSpacing]}
        >
          <Text size="xs" color="grey">
            {toDdMmYyyy(new Date(date))}
          </Text>
          <Text size="xs" color="grey">
            {city}
          </Text>
        </View>
        <Text size="xs" bold color={type === 'TOKO' ? 'blue' : 'orange'}>
          {t(['Tipe', 'Type'])}: {type === 'USER' ? 'OWNER' : type}
        </Text>
      </View>
    </TouchableWithoutFeedback>
  );
};
type Props = {
  shouldRefetch: boolean;
  selectedItemId: string | null;
  setSelectedItem: (
    value: VerificationRequests_requestVerificationAdvance_row | null,
  ) => void;
  onFilterChange: (filter: VerificationRequestsVariables) => void;
} & VerificationRequestsQueryResult;

export let defaultWipFilterVal = {
  confirmDateInput: null,
  registerDateInput: null,
  storeFilter: null,
  type: null,
  viewData: FilterViewDataInput.ALL,
  pagination: {
    skip: 0,
    first: 8,
  },
  userStatus: [
    FilterUserStatusVerifyInput.UNVERIFIED,
    FilterUserStatusVerifyInput.WAITING,
  ],
  storeStatus: [
    FilterStoreStatusVerifyInput.UNVERIFIED,
    FilterStoreStatusVerifyInput.WAITING,
    FilterStoreStatusVerifyInput.REVALIDATION,
  ],
};

const LeftPane = (props: Props) => {
  const {
    selectedItemId,
    setSelectedItem,
    data,
    onFilterChange,
    refetch,
    error,
    loading,
    fetchMore,
    networkStatus,
  } = props;
  const { id: adminId } = useAuth();
  let [searchTerm, setSearchTerm] = useState('');
  const rowsPerPage = 8;
  // let [page, setPage] = useState(0);

  let [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  let [filterOpen, setFilterOpen] = useState(false);
  let [filterVariables, setFilterVariables] = useState<
    VerificationRequestsVariables
  >({
    searchName: debouncedSearchTerm,
    adminId,
    ...defaultWipFilterVal,
  });

  const [filterPlaceholder, setFilterPlaceholder] = useState('');

  useEffect(() => {
    onFilterChange({
      ...filterVariables,
    });
  }, [filterVariables, onFilterChange]);

  useEffect(() => {
    if (
      error &&
      (error.networkError as CustomNetworkError)?.result?.errors.find(
        (item: { message: string }) =>
          item.message.includes('Phone Number must start with 08'),
      )
    ) {
    }
  }, [error]);

  useEffect(() => {
    if (
      filterVariables.searchName !== debouncedSearchTerm &&
      (debouncedSearchTerm === '' || debouncedSearchTerm.trim() !== '')
    ) {
      setSelectedItem(null);
      setFilterVariables({
        ...filterVariables,
        searchName: debouncedSearchTerm,
      });
    }
  }, [debouncedSearchTerm, setSelectedItem, filterVariables]);

  let onSetFilterVariables = useCallback(
    (params: VerificationRequestsVariables) => {
      setFilterVariables({
        ...filterVariables,
        ...params,
        adminId,
        searchName: filterVariables.searchName,
        userStatus: defaultWipFilterVal.userStatus,
        storeStatus: defaultWipFilterVal.storeStatus,
      });
    },
    [setFilterVariables, filterVariables, adminId],
  );

  let onCloseFilter = useCallback(() => setFilterOpen(false), [setFilterOpen]);

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <View style={[styles.row, styles.mediumBottomSpacing]}>
          <Text bold size="xl">
            {t(['Sedang Dikerjakan', 'Work In Progress'])}
          </Text>
          <View style={styles.badge}>
            <Text color="white" size="xs">
              {data.requestVerificationAdvance.total}
            </Text>
          </View>
        </View>
        <TextInput
          data-testid="filter-form-toko-owner"
          value={searchTerm}
          onChangeText={(value: string) => {
            setSearchTerm(value);
          }}
          placeholder={t(['Cari', 'Search'])}
          rightNode={<SearchRoundedIcon htmlColor={colors.leftPane.icon} />}
          containerStyle={styles.mediumBottomSpacing}
        />
        <Filter
          onPress={() => setFilterOpen(true)}
          placeholder={filterPlaceholder}
        />
      </View>
      {(loading && data.requestVerificationAdvance.row.length === 0) ||
      (loading && (networkStatus === 2 || networkStatus === 4)) ? (
        <ActivityIndicator style={styles.center} />
      ) : (
        <FlatList
          data={data.requestVerificationAdvance.row}
          renderItem={({ item: verificationRequest }) => {
            const { id, type, store, user, createdAt } = verificationRequest;
            return (
              <ListItem
                key={id}
                name={
                  (type === TypeVerify.TOKO
                    ? store?.storeManagerBulk?.storeName
                    : user?.profile?.name) || ''
                }
                date={createdAt}
                type={type}
                city={store?.storeManagerBulk?.city || ''}
                selected={id === selectedItemId}
                onSelect={() => setSelectedItem(verificationRequest)}
              />
            );
          }}
          ListEmptyComponent={
            <Text style={styles.center}>
              {t(['Tidak ada data', 'No data'])}
            </Text>
          }
          onEndReachedThreshold={0.1}
          onEndReached={() => {
            if (
              !loading &&
              data.requestVerificationAdvance.row.length <
                data.requestVerificationAdvance.total
            ) {
              fetchMore({
                variables: {
                  pagination: {
                    first: rowsPerPage,
                    skip: data.requestVerificationAdvance.row.length,
                  },
                },
                updateQuery: (prevData, { fetchMoreResult }) => ({
                  requestVerificationAdvance: {
                    ...prevData.requestVerificationAdvance,
                    ...fetchMoreResult?.requestVerificationAdvance,
                    row: [
                      ...prevData.requestVerificationAdvance.row,
                      ...(fetchMoreResult?.requestVerificationAdvance.row ||
                        []),
                    ],
                  },
                }),
              });
            }
          }}
          ListFooterComponent={
            networkStatus === 3 ||
            (loading &&
              data.requestVerificationAdvance.row.length <
                data.requestVerificationAdvance.total) ? (
              <ActivityIndicator
                style={[styles.center, { paddingBottom: 10 }]}
              />
            ) : (
              <></>
            )
          }
        />
      )}
      {error && (
        <ErrorMessage
          action={t([
            'mengambil data permintaan verifikasi',
            'retrieve the verification request data',
          ])}
          error={error}
          onPress={refetch}
          style={styles.center}
        />
      )}
      <FilterModal
        wip
        fullWidth
        open={filterOpen}
        setFilter={onSetFilterVariables}
        setPlaceholder={setFilterPlaceholder}
        onClose={onCloseFilter}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderColor: colors.border.primary,
  },
  header: {
    paddingHorizontal: spacing.l,
    paddingVertical: spacing.m,
    borderBottomWidth: 1,
    borderColor: colors.border.primary,
    backgroundColor: colors.background.white,
  },
  smallBottomSpacing: {
    paddingBottom: spacing.xxs,
  },
  mediumBottomSpacing: {
    paddingBottom: spacing.m,
  },
  largeBottomSpacing: {
    paddingBottom: spacing.l,
  },
  badge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.leftPane.badge,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: spacing.xxs,
  },
  listItem: {
    padding: spacing.l,
    borderBottomWidth: 1,
    borderColor: colors.border.primary,
    backgroundColor: colors.background.white,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  spaceBetween: {
    justifyContent: 'space-between',
  },
  selectedToko: {
    backgroundColor: colors.leftPane.toko.background,
    borderLeftWidth: 4,
    borderLeftColor: colors.leftPane.toko.leftBorder,
  },
  selectedOwner: {
    backgroundColor: colors.leftPane.owner.background,
    borderLeftWidth: 4,
    borderLeftColor: colors.leftPane.owner.leftBorder,
  },
  center: {
    alignSelf: 'center',
    paddingTop: spacing.l,
  },
});

export default LeftPane;
